import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';

interface User {
  id: number;
  username: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (userData: User, accessToken: string) => void;
  updateUser: (userData: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // Helper pour récupérer le token depuis les cookies
  const getTokenFromCookie = (): string | null => {
    const match = /access_token=([^;]+)/.exec(document.cookie);
    return match ? decodeURIComponent(match[1]) : null;
  };

  // Helper pour récupérer le user depuis les cookies (fallback pour compatibilité)
  const getUserFromCookie = (): User | null => {
    const match = /user=([^;]+)/.exec(document.cookie);
    if (match) {
      try {
        return JSON.parse(decodeURIComponent(match[1]));
      } catch {
        return null;
      }
    }
    return null;
  };

  // Au chargement initial, récupérer token et user depuis les cookies
  useEffect(() => {
    const savedToken = getTokenFromCookie();
    const savedUser = getUserFromCookie();
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(savedUser);
    }
  }, []);

  const login = (userData: User, accessToken: string) => {
    // Stocker le token en cookie (7 jours)
    document.cookie = `access_token=${encodeURIComponent(accessToken)}; path=/; max-age=604800; SameSite=Strict`;
    // Stocker aussi le user en cookie pour persistance (optionnel, mais pratique)
    document.cookie = `user=${encodeURIComponent(JSON.stringify(userData))}; path=/; max-age=604800; SameSite=Strict`;
    setToken(accessToken);
    setUser(userData);
  };

  const updateUser = (userData: User) => {
    // Mettre à jour uniquement le user sans toucher au token
    document.cookie = `user=${encodeURIComponent(JSON.stringify(userData))}; path=/; max-age=604800; SameSite=Strict`;
    setUser(userData);
  };

  const logout = () => {
    // Supprimer les cookies
    document.cookie = 'access_token=; path=/; max-age=0';
    document.cookie = 'user=; path=/; max-age=0';
    setToken(null);
    setUser(null);
  };

  const value = useMemo(() => ({ user, token, login, updateUser, logout }), [user, token]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
